import { useState, useEffect, useCallback, useRef } from "react";
import EventEmitter from "events";
import { Services } from "@/types/serviceTypes";
import { emitServices } from "@/utils/services/emitServices.ts";

const eventBus = new EventEmitter();
let sharedServices: Services = [];
const serviceOwners = new Map<string, Set<symbol>>();
const instanceOwned = new Map<symbol, Set<string>>();

export const resetSharedServices = () => {
  sharedServices = [];
  serviceOwners.clear();
  instanceOwned.clear();
};

export function useSharedServices(): [
  Services,
  (update: Services | ((prevState: Services) => Services)) => void,
] {
  const instanceId = useRef(Symbol());

  const [services, setServices] = useState<Services>(sharedServices);

  useEffect(() => {
    let changed = false;
    const next = services.map((service) => {
      if (service.isMandatory && !service.selected) {
        changed = true;
        return { ...service, selected: true };
      }
      return service;
    });
    if (changed) {
      sharedServices = next;
      setServices(next);
      emitServices(next.filter((service) => service.selected));
    }
  }, [services]);

  useEffect(() => {
    const onPageShow = () => {
      emitServices(services.filter((s) => s.selected));
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [services]);

  useEffect(() => {
    const id = instanceId.current;
    instanceOwned.set(id, new Set());

    const handleUpdate = (newServices: Services) => setServices(newServices);
    eventBus.on("updateServices", handleUpdate);

    return () => {
      eventBus.removeListener("updateServices", handleUpdate);

      const ownedSkuIds = instanceOwned.get(id) ?? new Set<string>();
      let poolChanged = false;

      ownedSkuIds.forEach((skuId) => {
        const owners = serviceOwners.get(skuId);
        if (owners) {
          owners.delete(id);
          if (owners.size === 0) {
            serviceOwners.delete(skuId);
            sharedServices = sharedServices.filter((s) => s.skuId !== skuId);
            poolChanged = true;
          }
        }
      });

      instanceOwned.delete(id);
      if (poolChanged) eventBus.emit("updateServices", sharedServices);
    };
  }, []);

  const updateServices = useCallback(
    (update: Services | ((prevState: Services) => Services)) => {
      const id = instanceId.current;
      let newServices: Services;

      if (typeof update === "function") {
        newServices = update(sharedServices);
      } else {
        const newSkuIds = new Set(update.map((s) => s.skuId));
        const previouslyOwned = instanceOwned.get(id) ?? new Set<string>();
        let updatedServices = sharedServices;

        previouslyOwned.forEach((skuId) => {
          if (!newSkuIds.has(skuId)) {
            const owners = serviceOwners.get(skuId);
            if (owners) {
              owners.delete(id);
              if (owners.size === 0) {
                serviceOwners.delete(skuId);
                updatedServices = updatedServices.filter(
                  (s) => s.skuId !== skuId,
                );
              }
            }
          }
        });

        const existingSkuIds = new Set(updatedServices.map((s) => s.skuId));
        const newEntries: Services = [];

        update.forEach((service) => {
          let ownerSet = serviceOwners.get(service.skuId);
          if (!ownerSet) {
            ownerSet = new Set<symbol>();
            serviceOwners.set(service.skuId, ownerSet);
          }
          ownerSet.add(id);
          if (!existingSkuIds.has(service.skuId)) {
            newEntries.push(service);
            existingSkuIds.add(service.skuId);
          }
        });

        instanceOwned.set(id, newSkuIds);
        newServices = [...updatedServices, ...newEntries];
      }

      sharedServices = newServices;
      eventBus.emit("updateServices", newServices);
    },
    [],
  );

  return [services, updateServices];
}
